export const SONG_TYPE_CATEGORIES = ["psalms", "hymns", "spiritual-songs"] as const;

export type SongTypeCategory = (typeof SONG_TYPE_CATEGORIES)[number];

type SongTypeCategoryConfig = {
  slug: SongTypeCategory;
  label: string;
  sanityParam: string;
  heroTitle: string;
  heroCopy: string;
  emptyTitle: string;
  emptyCopy: string;
  heroAssetPath: string;
};

export type SongTypeDevotional = {
  _id: string;
  title: string | null;
  subtitle: string | null;
  releaseDate: string | null;
  startShowingDate: string | null;
  permissions: Array<string> | null;
  slug: {
    current: string | null;
  } | null;
  thumbnail: string | null;
  images: Array<{
    imageType?: string | null;
    imageName?: string | null;
    imageUrl?: string | null;
  }> | null;
  scriptureReferences: Array<{
    book?: string | null;
    startChapter?: number | null;
    startVerse?: number | null;
    endChapter?: number | null;
    endVerse?: number | null;
    esvText?: string | null;
  }> | null;
  songTypeSlug?: string | null;
  songTypeLabel?: string | null;
  totalCount?: number | null;
  series: Array<{
    _id: string;
    name: string | null;
    ownershipType: string | null;
    freePeriod: number | null;
    slug: {
      current: string | null;
    } | null;
    logomarkUrl: string | null;
    images: Array<{
      imageType?: string | null;
      imageName?: string | null;
      imageUrl?: string | null;
    }> | null;
  }> | null;
};

const SONG_TYPE_CATEGORY_CONFIG: Record<SongTypeCategory, SongTypeCategoryConfig> = {
  psalms: {
    slug: "psalms",
    label: "Psalms",
    sanityParam: "Psalms",
    heroTitle: "Sing the Psalms",
    heroCopy: "Devotionals tied to songs shaped by the language, prayers, and poetry of the Psalms.",
    emptyTitle: "No psalm devotionals yet.",
    emptyCopy: "Check back soon for devotionals in this song type.",
    heroAssetPath: "/song-types/psalms-card.svg",
  },
  hymns: {
    slug: "hymns",
    label: "Hymns",
    sanityParam: "Hymns",
    heroTitle: "Sing the Hymns",
    heroCopy: "Browse devotionals connected to songs rooted in historic doctrine and congregational singing.",
    emptyTitle: "No hymn devotionals yet.",
    emptyCopy: "Check back soon for devotionals in this song type.",
    heroAssetPath: "/song-types/hymns-card.svg",
  },
  "spiritual-songs": {
    slug: "spiritual-songs",
    label: "Spiritual Songs",
    sanityParam: "Spiritual Songs",
    heroTitle: "Sing Spiritual Songs",
    heroCopy: "Explore devotionals paired with songs of testimony, exhortation, and present-day response.",
    emptyTitle: "No spiritual song devotionals yet.",
    emptyCopy: "Check back soon for devotionals in this song type.",
    heroAssetPath: "/song-types/spiritual-songs-card.svg",
  },
};

export function isSongTypeCategory(value: string | null | undefined): value is SongTypeCategory {
  return SONG_TYPE_CATEGORIES.includes(value as SongTypeCategory);
}

export function getSongTypeCategoryConfig(category: SongTypeCategory): SongTypeCategoryConfig {
  return SONG_TYPE_CATEGORY_CONFIG[category];
}

export function getSongTypeHref(category: SongTypeCategory): string {
  return `/song-types?type=${category}`;
}
